import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { 
  Search, 
  Filter, 
  MessageSquare, 
  ExternalLink,
  Eye,
  AlertTriangle,
  Clock,
  User,
  Hash,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Edit
} from 'lucide-react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import './Messages.css';

// Utility function to format timestamps in US format (MM/DD/YYYY with AM/PM)
const formatAmericanTime = (timestamp) => {
  if (!timestamp) return 'N/A';
  
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  // Explicitly convert UTC to local time
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  
  return localDate.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};

// Utility function to format current time (already local)
const formatCurrentTime = (date) => {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });
};



const Messages = () => {
  const [filters, setFilters] = useState({
    risk_level: '',
    message_type: '',
    user_id: '',
    channel_id: '',
    search: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
  });
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewMessage, setReviewMessage] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');

  // Fetch messages
  const { data: messagesData, isLoading, refetch } = useQuery(
    ['messages', filters, pagination, sortBy, sortOrder],
    async () => {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.per_page.toString(),
      });
      
      // Only add filters that have actual values
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          params.append(key, value);
        }
      });
      
      if (sortBy && sortOrder) {
        params.append('sort_by', sortBy);
        params.append('sort_order', sortOrder);
      }
      
      return apiService.getMessages(Object.fromEntries(params));
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  // Manual message analysis
  const [analysisForm, setAnalysisForm] = useState({
    user_id: 'test_user',
    channel_id: 'test_channel',
    message_text: '',
  });
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingMetrics, setStreamingMetrics] = useState(null);
  const [streamingComplete, setStreamingComplete] = useState(false);
  const [encryptionStatus, setEncryptionStatus] = useState(null);
  const [merkleInfo, setMerkleInfo] = useState(null);
  const [completionId, setCompletionId] = useState(null);
  const [debugData, setDebugData] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [analysisTimeout, setAnalysisTimeout] = useState(null);
  const analysisCompletedRef = useRef(false);
  const successToastShownRef = useRef(false);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const handleTraceOnBlockchain = (completionId) => {
    if (!completionId) return;
    
    // Convert completion ID to blockchain address
    const blockchainAddress = completionId.replace('chatcmpl-', '');
    const explorerUrl = `https://explorer.ambient.xyz/address/${blockchainAddress}`;
    
    window.open(explorerUrl, '_blank');
  };

  const handleReviewClick = (message) => {
    setReviewMessage(message);
    setReviewComment(message.review_comment || '');
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewComment.trim()) {
      toast.error('Please enter a review comment');
      return;
    }

    try {
      const response = await apiService.submitReview(reviewMessage.id, {
        review_comment: reviewComment.trim()
      });

      if (response.success) {
        toast.success('Review submitted successfully');
        setShowReviewModal(false);
        setReviewMessage(null);
        setReviewComment('');
        refetch(); // Refresh the messages list
      }
    } catch (error) {
      toast.error('Failed to submit review');
      console.error('Review submission error:', error);
    }
  };

  const handleAnalyzeMessage = async (e) => {
    e.preventDefault();
    setIsAnalyzing(true);
    setStreamingContent('');
    setStreamingMetrics(null);
    setStreamingComplete(false);
    setAnalysisResult(null);
    setEncryptionStatus(null);
    setMerkleInfo(null);
    setCompletionId(null);
    setDebugData([]);
    setShowDebug(false);
    setAnalysisStatus('Initializing AI analysis...');
    
    // Clear any existing timeout
    if (analysisTimeout) {
      clearTimeout(analysisTimeout);
      setAnalysisTimeout(null);
    }
    
    // Reset completion flags
    analysisCompletedRef.current = false;
    successToastShownRef.current = false;
    
    // Set 60-second timeout
    const timeout = setTimeout(() => {
      // Only show timeout error if analysis hasn't completed
      if (!analysisCompletedRef.current) {
        setIsAnalyzing(false);
        setAnalysisStatus('');
        toast.error('Analysis timed out after 60 seconds');
      }
    }, 60000);
    setAnalysisTimeout(timeout);
    
    try {
      setAnalysisStatus('Connecting to AI service...');

      await apiService.analyzeMessageStream(analysisForm, (data) => {
        console.log('Streaming data received:', data);
        setDebugData(prev => [...prev, data]);
        
        switch (data.type) {
          case 'content_chunk':
            // Don't accumulate content chunks - they contain the full AI process
            // Only show a brief status update
            setAnalysisStatus('Analyzing response...');
            break;
          case 'metrics_update':
            setStreamingMetrics(data.data);
            // Show only the reasoning from metrics update
            if (data.data.reasoning) {
              setStreamingContent(data.data.reasoning);
            }
            setAnalysisStatus('Processing results...');
            break;
          case 'analysis_complete':
            // Always process the data
            setStreamingComplete(true);
            setAnalysisResult(data.data);
            // Show final reasoning from analysis complete
            if (data.data.reasoning) {
              setStreamingContent(data.data.reasoning);
            }
            setAnalysisStatus('Analysis complete');
            // Clear timeout since analysis is complete
            if (analysisTimeout) {
              clearTimeout(analysisTimeout);
              setAnalysisTimeout(null);
            }
            // Check if completion ID in the final result
            if (data.data.completion_id) {
              setCompletionId(data.data.completion_id);
            }
            // Mark as completed
            analysisCompletedRef.current = true;
            // Show success toast only once
            if (!successToastShownRef.current) {
              successToastShownRef.current = true;
              if (data.data.analysis_success === false || data.data.error) {
                toast.error('Analysis failed: ' + (data.data.error || 'Unknown error'));
              } else {
                toast.success('Message analyzed successfully');
              }
            }
            refetch();
            break;
          case 'analysis_chunk':
            // Handle analysis chunks (final results)
            if (!data.is_partial) {
              // Always process the data
              setStreamingComplete(true);
              setAnalysisResult(data.data);
              // Show final reasoning from analysis chunk
              if (data.data.reasoning) {
                setStreamingContent(data.data.reasoning);
              }
              setAnalysisStatus('Analysis complete');
              // Clear timeout since analysis is complete
              if (analysisTimeout) {
                clearTimeout(analysisTimeout);
                setAnalysisTimeout(null);
              }
              // Mark as completed
              analysisCompletedRef.current = true;
              // Show success toast only once
              if (!successToastShownRef.current) {
                successToastShownRef.current = true;
                if (data.data.error) {
                  toast.error('Analysis failed: ' + (data.data.error || 'Unknown error'));
                } else {
                  toast.success('Message analyzed successfully');
                }
              }
              refetch();
            }
            break;
          case 'usage':
            // Handle usage data (blockchain/completion info)
            if (data.usage_data) {
              if (data.usage_data.completion_id) {
                setCompletionId(data.usage_data.completion_id);
                setAnalysisStatus('Finalizing blockchain transaction...');
              }
              if (data.usage_data.merkle_root) {
                setMerkleInfo({ merkle_root: data.usage_data.merkle_root });
                setAnalysisStatus('Generating blockchain proof...');
              }
              // Set encryption status if available
              setEncryptionStatus({ status: 'success', message: 'Encryption successful' });
              setAnalysisStatus('Securing blockchain data...');
            }
            break;
          case 'error':
            // Handle error events from streaming
            if (!analysisCompletedRef.current) {
              analysisCompletedRef.current = true;
              setAnalysisStatus('Analysis failed');
              if (analysisTimeout) {
                clearTimeout(analysisTimeout);
                setAnalysisTimeout(null);
              }
              // Show error toast only once
              if (!successToastShownRef.current) {
                successToastShownRef.current = true;
                toast.error('Analysis error: ' + (data.message || data.error || 'Unknown error'));
              }
            }
            break;
          default:
            console.log('Unknown data type:', data.type);
            // Update status for any received data
            setAnalysisStatus('Processing AI response...');
            break;
        }
      });
    } catch (error) {
      if (!analysisCompletedRef.current) {
        analysisCompletedRef.current = true;
        // Show error toast only once
        if (!successToastShownRef.current) {
          successToastShownRef.current = true;
          toast.error('Failed to analyze message');
        }
        console.error('Analysis error:', error);
      }
    } finally {
      setIsAnalyzing(false);
      setAnalysisStatus('');
      if (analysisTimeout) {
        clearTimeout(analysisTimeout);
        setAnalysisTimeout(null);
      }
    }
  };

  const RiskBadge = ({ score }) => {
    const getRiskLevel = (score) => {
        if (score >= 80) return { level: 'CRITICAL', color: '#a35252' };
  if (score >= 70) return { level: 'HIGH', color: '#a35252' };
  if (score >= 50) return { level: 'MEDIUM', color: '#a36b52' };
  if (score >= 20) return { level: 'LOW', color: '#a3a352' };
  return { level: 'NONE', color: '#00a352' };
    };

    const { level, color } = getRiskLevel(score);
    return (
      <span className="risk-badge" style={{ backgroundColor: color }}>
        {score}
      </span>
    );
  };

  const SortableHeader = ({ column, children }) => (
    <th 
      className="sortable-header"
      onClick={() => handleSort(column)}
    >
      <div className="header-content">
        {children}
        <div className="sort-indicator">
          {sortBy === column ? (
            sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
          ) : (
            <div className="sort-placeholder" />
          )}
        </div>
      </div>
    </th>
  );

  return (
    <div className="messages-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Message Analysis</h1>
          <div className="header-actions">
            <div className="last-updated">
              <Clock className="clock-icon" />
              <span>Updated {formatCurrentTime(new Date())}</span>
            </div>
            <button 
              className="refresh-btn"
              onClick={() => refetch()}
              disabled={isLoading}
              title="Refresh messages"
            >
              <RefreshCw className={`refresh-icon ${isLoading ? 'spinning' : ''}`} size={16} />
              Refresh
            </button>
            <button 
              className="refresh-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="refresh-icon" size={16} />
              Filters
            </button>
          </div>
        </div>
      </div>

      {/* Manual Analysis Form */}
      <div className="analysis-section">
        <div className="card">
          <div className="card-header">
            <h3>Manual Compliance Check</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleAnalyzeMessage} className="analysis-form">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">User ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={analysisForm.user_id}
                    onChange={(e) => setAnalysisForm(prev => ({ ...prev, user_id: e.target.value }))}
                    placeholder="e.g., trader_001"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Channel ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={analysisForm.channel_id}
                    onChange={(e) => setAnalysisForm(prev => ({ ...prev, channel_id: e.target.value }))}
                    placeholder="e.g., trading_room"
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">Message Text</label>
                  <textarea
                    className="form-control"
                    value={analysisForm.message_text}
                    onChange={(e) => setAnalysisForm(prev => ({ ...prev, message_text: e.target.value }))}
                    placeholder="Enter message to analyze..."
                    rows={3}
                    required
                  />
                </div>
              </div>
              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-success"
                  disabled={isAnalyzing}
                >
                  {isAnalyzing ? 'Analyzing...' : 'Analyze Message'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setAnalysisForm({ user_id: 'test_user', channel_id: 'test_channel', message_text: '' })}
                >
                  Reset Form
                </button>
              </div>
            </form>

            {/* Analysis Result */}
            {(isAnalyzing || streamingContent || streamingMetrics || analysisResult || encryptionStatus || merkleInfo || debugData.length > 0) && (
              <div className="analysis-result">
                <h4>Analysis Result</h4>
                
                {/* Analysis Status */}
                {isAnalyzing && (
                  <div className="analyzing-status">
                    <div className="analyzing-indicator">
                      <RefreshCw className="spinning" size={16} />
                      {analysisStatus || 'Analyzing message...'}
                    </div>
                  </div>
                )}
                
                {/* Streaming Content */}
                {streamingContent && (
                  <div className="streaming-content">
                    <strong>AI Analysis:</strong>
                    <div className="content-text">
                      {streamingContent}
                      {isAnalyzing && <span className="cursor">|</span>}
                    </div>
                  </div>
                )}
                
                {/* Debug Data */}
                {debugData.length > 0 && (
                  <div className="debug-section">
                    <button 
                      className="debug-toggle"
                      onClick={() => setShowDebug(!showDebug)}
                    >
                      {showDebug ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      Debug Data ({debugData.length} events)
                    </button>
                    {showDebug && (
                      <div className="debug-content">
                        {debugData.map((data, index) => (
                          <div key={index} className="debug-item">
                            <strong>Type: {data.type}</strong>
                            <pre>{JSON.stringify(data, null, 2)}</pre>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                
                
                {/* Live Metrics */}
                {streamingMetrics && (
                  <div className="result-grid">
                    <div className="result-item">
                      <strong>Risk Score:</strong>
                      <RiskBadge score={streamingMetrics.score || streamingMetrics.risk_score} />
                    </div>
                    <div className="result-item">
                      <strong>Risk Level:</strong>
                      <span className="risk-level">{streamingMetrics.classification || streamingMetrics.risk_level}</span>
                    </div>
                    <div className="result-item">
                      <strong>Violations:</strong>
                      <div className="violations">
                        {(streamingMetrics.violations || streamingMetrics.compliance_violations || []).map((violation, index) => (
                          <span key={index} className="violation-tag">
                            {violation}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Final Result */}
                {analysisResult && streamingComplete && (
                  <div className="final-result">
                    {analysisResult.analysis_success === false || analysisResult.error ? (
                      <div className="error-result">
                        <div className="error-header">
                          <strong>Analysis Failed</strong>
                        </div>
                        <div className="error-message">
                          {analysisResult.error || 'Unknown error occurred during analysis'}
                        </div>
                        {analysisResult.risk_score && (
                          <div className="partial-result">
                            <small>Partial results may be available below:</small>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="result-grid">
                        <div className="result-item">
                          <strong>Final Risk Score:</strong>
                          <RiskBadge score={analysisResult.risk_score} />
                        </div>
                        <div className="result-item">
                          <strong>Final Risk Level:</strong>
                          <span className="risk-level">{analysisResult.risk_level}</span>
                        </div>
                        <div className="result-item">
                          <strong>Final Violations:</strong>
                          <div className="violations">
                            {analysisResult.compliance_violations?.map((violation, index) => (
                              <span key={index} className="violation-tag">
                                {violation}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    
                                    {/* Trace on Blockchain Button */}
                {completionId && (
                  <div className="trace-section">
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleTraceOnBlockchain(completionId)}
                    >
                      <ExternalLink className="btn-icon" />
                      Trace on Blockchain
                    </button>
                    <div className="completion-info">
                      <small>Completion ID: {completionId}</small>
                    </div>
                  </div>
                )}
                

                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-section">
          <div className="card">
            <div className="card-header">
              <h3>Filters & Search</h3>
            </div>
            <div className="card-body">
              <div className="filters-grid">
                <div className="form-group">
                  <label className="form-label">Search</label>
                  <div className="search-input">
                    <Search className="search-icon" />
                    <input
                      type="text"
                      className="form-control"
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      placeholder="Search messages..."
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Risk Level</label>
                  <select
                    className="form-control"
                    value={filters.risk_level}
                    onChange={(e) => handleFilterChange('risk_level', e.target.value)}
                  >
                    <option value="">All Levels</option>
                    <option value="NONE">None</option>
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Message Type</label>
                  <select
                    className="form-control"
                    value={filters.message_type}
                    onChange={(e) => handleFilterChange('message_type', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="TRADE_REQUEST">Trade Request</option>
                    <option value="TRADE_COLOR">Trade Color</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">User ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={filters.user_id}
                    onChange={(e) => handleFilterChange('user_id', e.target.value)}
                    placeholder="Filter by user..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Channel ID</label>
                  <input
                    type="text"
                    className="form-control"
                    value={filters.channel_id}
                    onChange={(e) => handleFilterChange('channel_id', e.target.value)}
                    placeholder="Filter by channel..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Results per page</label>
                  <select
                    className="form-control"
                    value={pagination.per_page}
                    onChange={(e) => setPagination(prev => ({ ...prev, per_page: parseInt(e.target.value), page: 1 }))}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Messages Table */}
      <div className="messages-section">
        <div className="section-header">
          <h2>Messages ({messagesData?.total || 0})</h2>
          <div className="table-info">
            <span>Showing {((pagination.page - 1) * pagination.per_page) + 1} - {Math.min(pagination.page * pagination.per_page, messagesData?.total || 0)} of {messagesData?.total || 0}</span>
          </div>
        </div>

        <div className="messages-table">
          <table className="table">
            <thead>
              <tr>
                <SortableHeader column="timestamp">Time</SortableHeader>
                <SortableHeader column="user_id">User</SortableHeader>
                <SortableHeader column="channel_id">Channel</SortableHeader>
                <th>Message</th>
                <SortableHeader column="risk_score">Risk Score</SortableHeader>
                <th>Violations</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    <div className="loading-spinner"></div>
                    Loading messages...
                  </td>
                </tr>
              ) : messagesData?.messages?.length > 0 ? (
                messagesData.messages.map((message) => (
                  <tr key={message.id} className="message-row">
                    <td>
                      <div className="timestamp">
                        <Clock className="timestamp-icon" />
                        <span>{formatAmericanTime(message.timestamp)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="user-info">
                        <User className="user-icon" />
                        <span>{message.user_name || message.user_id}</span>
                      </div>
                    </td>
                    <td>
                      <div className="channel-info">
                        <Hash className="channel-icon" />
                        <span>{message.channel_id}</span>
                      </div>
                    </td>
                    <td>
                      <div className="message-content">
                        <div className="message-text">
                          {message.message_text}
                        </div>
                      </div>
                    </td>
                    <td>
                      <RiskBadge score={message.risk_score} />
                    </td>
                    <td>
                      <div className="violations">
                        {message.compliance_violations?.map((violation, index) => (
                          <span key={index} className="violation-tag">
                            {violation}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div className="actions">
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => setSelectedMessage(message)}
                        >
                          <Eye className="btn-icon" />
                          Details
                        </button>
                        <button 
                          className={`btn btn-sm ${message.review_comment ? 'btn-warning' : 'btn-secondary'}`}
                          onClick={() => handleReviewClick(message)}
                          title={message.review_comment ? 'Edit Review' : 'Add Review'}
                        >
                          <Edit className="btn-icon" />
                          {message.review_comment ? 'Review' : 'Review'}
                        </button>
                        {message.completion_id && (
                          <button 
                            className="btn btn-sm btn-success"
                            onClick={() => handleTraceOnBlockchain(message.completion_id)}
                          >
                            <ExternalLink className="btn-icon" />
                            Trace
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center text-muted">
                    No messages found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {messagesData?.total > pagination.per_page && (
          <div className="pagination">
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
            >
              Previous
            </button>
            <span className="page-info">
              Page {pagination.page} of {Math.ceil(messagesData.total / pagination.per_page)}
            </span>
            <button 
              className="btn btn-secondary btn-sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= Math.ceil(messagesData.total / pagination.per_page)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="modal-overlay" onClick={() => setSelectedMessage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Message Details</h3>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => setSelectedMessage(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="message-detail-grid">
                <div className="detail-item">
                  <strong>User:</strong>
                  <span>{selectedMessage.user_name || selectedMessage.user_id}</span>
                </div>
                <div className="detail-item">
                  <strong>Channel:</strong>
                  <span>{selectedMessage.channel_id}</span>
                </div>
                                    <div className="detail-item">
                      <strong>Time:</strong>
                      <span>{formatAmericanTime(selectedMessage.timestamp)}</span>
                    </div>
                <div className="detail-item">
                  <strong>Risk Score:</strong>
                  <RiskBadge score={selectedMessage.risk_score} />
                </div>
                <div className="detail-item full-width">
                  <strong>Message:</strong>
                  <div className="full-message">{selectedMessage.message_text}</div>
                </div>
                <div className="detail-item full-width">
                  <strong>Violations:</strong>
                  <div className="violations">
                    {selectedMessage.compliance_violations?.map((violation, index) => (
                      <span key={index} className="violation-tag">
                        {violation}
                      </span>
                    ))}
                  </div>
                </div>
                {selectedMessage.compliance_reasoning && (
                  <div className="detail-item full-width">
                    <strong>Analysis:</strong>
                    <div className="reasoning">
                      {selectedMessage.compliance_reasoning}
                    </div>
                  </div>
                )}
                {selectedMessage.completion_id && (
                  <div className="detail-item full-width">
                    <strong>Completion ID:</strong>
                    <div className="completion-info">
                      {selectedMessage.completion_id}
                    </div>
                  </div>
                )}
                {selectedMessage.completion_id && (
                  <div className="detail-item full-width">
                    <strong>Blockchain:</strong>
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleTraceOnBlockchain(selectedMessage.completion_id)}
                    >
                      <ExternalLink className="btn-icon" />
                      Trace on Blockchain
                    </button>
                  </div>
                )}
                {selectedMessage.review_comment && (
                  <div className="detail-item full-width">
                    <strong>Review Comment:</strong>
                    <div className="review-comment">
                      {selectedMessage.review_comment}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && reviewMessage && (
        <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Review Message</h3>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => setShowReviewModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="review-form">
                <div className="message-preview">
                  <strong>Message:</strong>
                  <div className="message-text">{reviewMessage.message_text}</div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Review Comment</label>
                  <textarea
                    className="form-control"
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Enter your review comment explaining why you disagree with the AI analysis..."
                    rows={4}
                  />
                </div>

                <div className="quick-reasons">
                  <label className="form-label">Quick Reasons (click to add):</label>
                  <div className="quick-reason-buttons">
                    {[
                      "AI misinterpreted the context",
                      "Risk score too high for this message",
                      "No actual compliance violation",
                      "Message is standard business communication",
                      "False positive detection"
                    ].map((reason, index) => (
                      <button
                        key={index}
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setReviewComment(prev => prev + (prev ? '\n' : '') + reason)}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowReviewModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSubmitReview}
              >
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages; 